import React, {useState, useEffect, useRef, useCallback} from 'react'
import {Input, FormGroup, Label} from 'reactstrap'
import FontPicker from 'font-picker-react'
import {removeNode, updateNode, updateText} from '../../../../editorActions'
import NodeLayerHeader from './NodeLayerHeader'

function NodeToolboxHeader({node, onRemove, onLockToggle}) {
    return (
        <NodeLayerHeader
            node={node}
            className="SidebarPanel__NodeToolboxHeader"
            subtitleVariant="toolbox"
            onRemove={onRemove}
            onLockToggle={onLockToggle}
        />
    )
}

function roundToTwo(value) {
    if (typeof value !== 'number' || Number.isNaN(value)) return value
    return Math.round(value * 100) / 100
}

function InputWithUnit({label, value, unit, onChange, step = 1, min, max}) {
    const [localValue, setLocalValue] = useState(null)

    const valueFromProp =
        value === undefined || value === null
            ? ''
            : typeof value === 'number' && !Number.isNaN(value)
              ? Number(value).toFixed(2)
              : String(value)

    useEffect(() => {
        setLocalValue(null)
    }, [value])

    const applyClamp = (num) => {
        if (typeof min === 'number' && !Number.isNaN(min) && num < min) return min
        if (typeof max === 'number' && !Number.isNaN(max) && num > max) return max
        return num
    }

    const commitValue = (num) => {
        const rounded = roundToTwo(num)
        const clamped = applyClamp(rounded)
        onChange(clamped)
    }

    const handleFocus = () => {
        setLocalValue(valueFromProp)
    }

    const handleChange = (e) => {
        const v = e.target.value
        const num = v === '' || v === '-' ? '' : parseFloat(v)
        if (num !== '' && Number.isNaN(num)) return
        setLocalValue(v)
    }

    const handleBlur = () => {
        if (localValue === null) return
        const v = localValue.trim()
        const num = v === '' || v === '-' ? '' : parseFloat(v)
        setLocalValue(null)
        if (num === '' || num === '-') {
            onChange(v)
            return
        }
        if (!Number.isNaN(num)) commitValue(num)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            const raw = localValue !== null ? localValue : valueFromProp
            const current =
                raw === '' || raw === '-'
                    ? 0
                    : (() => {
                          const n = parseFloat(raw)
                          return Number.isNaN(n) ? 0 : n
                      })()
            const next = e.key === 'ArrowUp' ? current + step : current - step
            const clamped = applyClamp(roundToTwo(next))
            e.preventDefault()
            setLocalValue(null)
            onChange(clamped)
        }
    }

    const displayValue = localValue !== null ? localValue : valueFromProp

    return (
        <div className="SidebarPanel__NodeInputRow">
            <label className="SidebarPanel__NodeInputLabel">{label}</label>
            <div className="SidebarPanel__NodeInputWrap">
                <input
                    type="text"
                    className="SidebarPanel__NodeInput"
                    value={displayValue}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    aria-label={label}
                />
                <span className="SidebarPanel__NodeInputUnit">{unit}</span>
            </div>
        </div>
    )
}

const STROKE_WIDTH_MIN = 0
const STROKE_WIDTH_MAX = 50

/** Google Fonts API URL used to pre-check availability (avoids "Response has status code 0" from font-manager XHR). */
const GOOGLE_FONTS_LIST_URL = 'https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity'

/** Fallback font list when Google Fonts API is unreachable (CORS, network, status 0). */
const FALLBACK_FONT_FAMILIES = [
    'Arial',
    'Helvetica',
    'Georgia',
    'Times New Roman',
    'Verdana',
    'Courier New',
    'Trebuchet MS',
    'Impact',
    'Comic Sans MS',
]

/** Capitalize first letter of each word (for text transform). */
function capitalizeText(text) {
    if (!text || typeof text !== 'string') return text
    return text
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

function StrokeSilhouetteSection({node, dispatch}) {
    const style = node.style || {}
    const strokeWidth = style.strokeWidth ?? 0

    const updateStyle = (patch) => {
        dispatch(updateNode(node.id, {style: {...style, ...patch}}))
    }

    return (
        <div className="SidebarPanel__NodeToolboxSection">
            <div className="SidebarPanel__SectionLabel">Stroke (Silhouette only)</div>
            <FormGroup className="SidebarPanel__StrokeRow">
                <Label for="stroke-width-slider" className="SidebarPanel__StrokeSliderLabel">
                    Width: {Number(strokeWidth).toFixed(2)} px
                </Label>
                <Input
                    id="stroke-width-slider"
                    type="range"
                    min={STROKE_WIDTH_MIN}
                    max={STROKE_WIDTH_MAX}
                    value={strokeWidth}
                    onChange={(e) => updateStyle({strokeWidth: Number(e.target.value)})}
                    className="SidebarPanel__StrokeSlider"
                    aria-label="Stroke width"
                />
            </FormGroup>
            <div className="SidebarPanel__SectionNote">Black stroke only. Visible in Silhouette view.</div>
        </div>
    )
}

function NodeToolboxText({node, dispatch}) {
    const geo = node.geometry || {}
    const style = node.style || {}
    const currentText = node.content?.text ?? node.style?.text ?? ''

    const [localText, setLocalText] = useState(currentText)
    const [textTransformActive, setTextTransformActive] = useState(null) // 'uppercase' | 'capitalize' | 'lowercase' | null
    const [googleFontsAvailable, setGoogleFontsAvailable] = useState(null) // null = pending, true = use FontPicker, false = use fallback
    const debounceRef = useRef(null)
    const lastNodeIdRef = useRef(node.id)

    useEffect(() => {
        if (lastNodeIdRef.current !== node.id) {
            lastNodeIdRef.current = node.id
            setLocalText(node.content?.text ?? node.style?.text ?? '')
            setTextTransformActive(null)
            return
        }
        setLocalText(node.content?.text ?? node.style?.text ?? '')
    }, [node.id, node.content?.text, node.style?.text])

    const flushText = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
    }, [])

    const dispatchText = useCallback(
        (text) => {
            if (text.trim() === '') return
            dispatch(updateText(node.id, text, {recordHistory: true}))
        },
        [dispatch, node.id],
    )

    const handleTextChange = (e) => {
        const val = e.target.value
        setLocalText(val)
        flushText()
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null
            dispatchText(val)
        }, 300)
    }

    const handleTextBlur = () => {
        flushText()
        dispatchText(localText)
    }

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    // Pre-check Google Fonts API to avoid "Response has status code 0" from font-picker-react's XHR
    const googleFontApiKeyForCheck = process.env.REACT_APP_GOOGLE_FONT_API_KEY || ''
    useEffect(() => {
        if (!googleFontApiKeyForCheck) {
            setGoogleFontsAvailable(false)
            return
        }
        let cancelled = false
        const url = `${GOOGLE_FONTS_LIST_URL}&key=${encodeURIComponent(googleFontApiKeyForCheck)}`
        fetch(url, {method: 'GET', mode: 'cors'})
            .then((res) => {
                if (cancelled) return
                setGoogleFontsAvailable(res.ok)
            })
            .catch(() => {
                if (!cancelled) setGoogleFontsAvailable(false)
            })
        return () => {
            cancelled = true
        }
    }, [googleFontApiKeyForCheck])

    const updateGeometry = (patch) => {
        dispatch(updateNode(node.id, {geometry: {...geo, ...patch}}))
    }

    const updateStyle = (patch) => {
        dispatch(updateNode(node.id, {style: {...style, ...patch}}))
    }

    const rotation = geo.rotation ?? 0
    const fontSize = style.fontSize ?? 60
    const fontFamily = style.fontFamily ?? 'Arial'
    const x = geo.x ?? 0
    const y = geo.y ?? 0

    const googleFontApiKey = process.env.REACT_APP_GOOGLE_FONT_API_KEY || ''
    const fontPickerId = `customShapeFontPicker${String(node.id).replace(/[^a-zA-Z0-9]/g, '') || '0'}`

    const handleFontChange = useCallback(
        async (font) => {
            const family = font?.family ?? font
            if (!family) return
            await document.fonts.ready
            updateStyle({fontFamily: family})
        },
        [updateStyle],
    )

    return (
        <div className="SidebarPanel__NodeToolboxBody">
            <div className="SidebarPanel__NodeInputRow">
                <label className="SidebarPanel__NodeInputLabel">Text</label>
                <textarea
                    className="SidebarPanel__NodeInput"
                    value={localText}
                    onChange={handleTextChange}
                    onBlur={handleTextBlur}
                    rows={2}
                    style={{resize: 'vertical', width: '100%'}}
                    aria-label="Text content"
                />
            </div>
            <div className="SidebarPanel__NodeInputRow">
                <label className="SidebarPanel__NodeInputLabel">Font</label>
                <div className="SidebarPanel__FontPickerWrap">
                    {!googleFontApiKey ? (
                        <button
                            type="button"
                            className="SidebarPanel__NodeToolboxAddFont"
                            disabled
                            aria-label="Font selector unavailable"
                            title="Google Fonts API key is missing. Set REACT_APP_GOOGLE_FONT_API_KEY in .env"
                        >
                            {fontFamily}
                        </button>
                    ) : googleFontsAvailable === true ? (
                        <FontPicker
                            apiKey={googleFontApiKey}
                            pickerId={fontPickerId}
                            activeFontFamily={fontFamily}
                            onChange={handleFontChange}
                        />
                    ) : (
                        <select
                            className="SidebarPanel__NodeInput SidebarPanel__FontPickerFallback"
                            value={fontFamily}
                            onChange={(e) => handleFontChange(e.target.value)}
                            aria-label="Font family"
                            title="Google Fonts unavailable; using system fonts"
                        >
                            {[...new Set([fontFamily, ...FALLBACK_FONT_FAMILIES.filter((f) => f !== fontFamily)])].map(
                                (f) => (
                                    <option key={f} value={f}>
                                        {f}
                                    </option>
                                ),
                            )}
                        </select>
                    )}
                </div>
            </div>
            <div className="SidebarPanel__NodeToolboxFontCase">
                <button
                    type="button"
                    className={`SidebarPanel__NodeToolboxFontCaseBtn ${textTransformActive === null ? 'is-active' : ''}`}
                    onClick={() => setTextTransformActive(null)}
                    title="No transform"
                    aria-label="No transform"
                >
                    −
                </button>
                <button
                    type="button"
                    className={`SidebarPanel__NodeToolboxFontCaseBtn ${textTransformActive === 'uppercase' ? 'is-active' : ''}`}
                    onClick={() => {
                        const next = localText.toUpperCase()
                        setTextTransformActive('uppercase')
                        setLocalText(next)
                        dispatchText(next)
                    }}
                    title="Uppercase"
                    aria-label="Uppercase"
                >
                    AA
                </button>
                <button
                    type="button"
                    className={`SidebarPanel__NodeToolboxFontCaseBtn ${textTransformActive === 'capitalize' ? 'is-active' : ''}`}
                    onClick={() => {
                        const next = capitalizeText(localText)
                        setTextTransformActive('capitalize')
                        setLocalText(next)
                        dispatchText(next)
                    }}
                    title="Capitalize"
                    aria-label="Capitalize"
                >
                    Aa
                </button>
                <button
                    type="button"
                    className={`SidebarPanel__NodeToolboxFontCaseBtn ${textTransformActive === 'lowercase' ? 'is-active' : ''}`}
                    onClick={() => {
                        const next = localText.toLowerCase()
                        setTextTransformActive('lowercase')
                        setLocalText(next)
                        dispatchText(next)
                    }}
                    title="Lowercase"
                    aria-label="Lowercase"
                >
                    aa
                </button>
            </div>
            <div className="SidebarPanel__NodeToolboxGrid">
                <InputWithUnit
                    label="Rotate"
                    value={rotation}
                    unit="deg"
                    step={1}
                    min={0}
                    max={360}
                    onChange={(v) =>
                        v !== '' &&
                        !Number.isNaN(Number(v)) &&
                        updateGeometry({rotation: Math.min(360, Math.max(0, Number(v)))})
                    }
                />
                <InputWithUnit
                    label="Font size"
                    value={fontSize}
                    unit="%"
                    step={1}
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateStyle({fontSize: Number(v)})}
                />
                <InputWithUnit
                    label="Position left"
                    value={x}
                    unit="%"
                    step={1}
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({x: Number(v)})}
                />
                <InputWithUnit
                    label="Position top"
                    value={y}
                    unit="%"
                    step={1}
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({y: Number(v)})}
                />
            </div>
            <StrokeSilhouetteSection node={node} dispatch={dispatch} />
        </div>
    )
}

function NodeToolboxImage({node, dispatch, onRemoveBackground}) {
    const geo = node.geometry || {}

    const updateGeometry = (patch) => {
        dispatch(updateNode(node.id, {geometry: {...geo, ...patch}}))
    }

    const width = geo.width ?? 100
    const height = geo.height ?? 100
    const rotation = geo.rotation ?? 0
    const x = geo.x ?? 0
    const y = geo.y ?? 0

    return (
        <div className="SidebarPanel__NodeToolboxBody">
            {/* {onRemoveBackground && (
                <div className="SidebarPanel__NodeToolboxActions">
                    <button
                        type="button"
                        className="SidebarPanel__NodeToolboxBtn SidebarPanel__NodeToolboxBtn--removeBg"
                        onClick={() => onRemoveBackground(node)}
                    >
                        Remove Background
                    </button>
                </div>
            )} */}
            <div className="SidebarPanel__NodeToolboxGrid">
                <InputWithUnit
                    label="Width"
                    value={width}
                    unit="W"
                    step={1}
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({width: Number(v)})}
                />
                <InputWithUnit
                    label="Height"
                    value={height}
                    unit="H"
                    step={1}
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({height: Number(v)})}
                />
                <InputWithUnit
                    label="Rotate"
                    value={rotation}
                    unit="deg"
                    step={1}
                    min={0}
                    max={360}
                    onChange={(v) =>
                        v !== '' &&
                        !Number.isNaN(Number(v)) &&
                        updateGeometry({rotation: Math.min(360, Math.max(0, Number(v)))})
                    }
                />
                <InputWithUnit
                    label="Position left"
                    value={x}
                    unit="%"
                    step={1}
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({x: Number(v)})}
                />
                <InputWithUnit
                    label="Position top"
                    value={y}
                    unit="%"
                    step={1}
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({y: Number(v)})}
                />
            </div>
            <StrokeSilhouetteSection node={node} dispatch={dispatch} />
        </div>
    )
}

function NodePropertiesToolbox({node, dispatch, inline, onRemoveBackground}) {
    const isText = node.type === 'text'

    const handleRemove = (nodeId) => {
        dispatch(removeNode(nodeId))
    }

    const handleLockToggle = (nodeId, locked) => {
        dispatch(updateNode(nodeId, {state: {locked}}))
    }

    const bodyContent = isText ? (
        <NodeToolboxText node={node} dispatch={dispatch} />
    ) : (
        <NodeToolboxImage node={node} dispatch={dispatch} onRemoveBackground={onRemoveBackground} />
    )

    return (
        <div className={`SidebarPanel__NodeToolbox${inline ? ' is-inline' : ''}`}>
            {!inline && <NodeToolboxHeader node={node} onRemove={handleRemove} onLockToggle={handleLockToggle} />}
            {bodyContent}
        </div>
    )
}

export default NodePropertiesToolbox
